import {
  getJoinCodeByCode as apiGetJoinCodeByCode,
  getJoinCodeByTeamId as apiGetJoinCodeByTeamId,
} from "../../api/joinCodeApi";
import { addTeamWithJoinCode as apiAddTeamWithJoinCode } from "../../api/teamApi";
import {
  getMemberCountByOrganization,
  hasEmailConflict as apiHasEmailConflict,
  joinTeam as apiJoinTeam,
} from "../../api/teamMemberApi";

/**
 * @param {string} code
 * @returns {Promise<{code: string, teamId: string, organizationId: string, leadUid: string|null}|null>}
 */
export const getJoinCodeByCode = async (code) => {
  return apiGetJoinCodeByCode(code);
};

/**
 * @param {string} teamId
 * @returns {Promise<{code: string}|null>} shaped for existing callers that read `.code`
 */
export const getJoinCodeByTeamId = async (teamId) => {
  const code = await apiGetJoinCodeByTeamId(teamId);
  return code ? { code } : null;
};

/**
 * Creates a team + join code under an existing organization.
 * @param {{ name: string, organizationId: string }} teamFields
 * @returns {Promise<{ teamId: string, code: string }>}
 */
export const addTeamWithJoinCode = async ({ name, organizationId }) => {
  return apiAddTeamWithJoinCode({ name, organizationId });
};

/**
 * @param {{ organizationId: string }} params
 * @returns {Promise<number>}
 */
export const getTeamUsersCountByOrganization = async ({ organizationId }) => {
  return getMemberCountByOrganization(organizationId);
};

/**
 * Validates join code, team, and team-lead subscription (same rules as join/subscribe flow).
 * @param {string} code
 * @returns {Promise<{ ok: true, organizationId: string, teamId: string } | { ok: false, error: string }>}
 */
export const validateJoinCodeEligibility = async (code) => {
  const trimmed = (code || "").trim();
  if (!trimmed) {
    return { ok: false, error: "Please enter a join code" };
  }

  const joinCodeDoc = await getJoinCodeByCode(trimmed);
  if (!joinCodeDoc) {
    return { ok: false, error: "no join-team code exist" };
  }

  const { default: SubscriptionVerificationService } =
    await import("../SubscriptionVerificationService");
  const leadVerification = {isValid: true}
    //await SubscriptionVerificationService.verifySubscription({
    //  joinCode: trimmed,
    //});

  if (!leadVerification.isValid) {
    return { ok: false, error: "The team is not active?" };
  }

  return {
    ok: true,
    organizationId: joinCodeDoc.organizationId,
    teamId: joinCodeDoc.teamId,
  };
};

/**
 * If someone else already belongs to this team with this email, block joining
 * with a different account.
 * @param {string} teamId
 * @param {string} normalizedEmail
 * @returns {Promise<{ ok: true } | { ok: false, error: string }>}
 */
export const verifyEmailAllowedForTeamJoin = async (teamId, normalizedEmail) => {
  const conflict = await apiHasEmailConflict({ teamId, email: normalizedEmail });
  if (conflict) {
    return {
      ok: false,
      error:
        "This email is already linked to another member on this team. Sign in with that account.",
    };
  }
  return { ok: true };
};

/**
 * Joins the caller (from the auth token) to a team and links their account to
 * its organization.
 * @param {{ organizationId: string, teamId: string }} params
 * @returns {Promise<{ ok: true } | { ok: false, error: string }>}
 */
export const completeTeamJoinForAuthenticatedUser = async ({
  organizationId,
  teamId,
}) => {
  return apiJoinTeam({ teamId, organizationId });
};
